import type { TranslationProgressMap } from '../types';
import {
  canonicalizeLanguageKey,
  canonicalizeLanguageName,
  canonicalizeTranslationProgressMap,
} from '../utils/languageIdentity';
import { getTargetLanguageFromPair } from '../utils/languagePairs';
import type { M2MHandoffRecord } from './types';

export const M2M_SUPPORTED_LANGUAGES = [
  'English',
  'German',
  'French',
  'Spanish',
  'European Spanish',
  'Latin American Spanish',
  'Italian',
  'Portuguese',
  'Dutch',
  'Japanese',
  'Swedish',
  'Polish',
  'Chinese',
] as const;

const M2M_SUPPORTED_LANGUAGE_KEYS = new Set(
  M2M_SUPPORTED_LANGUAGES.map((language) => canonicalizeLanguageKey(language))
);

export const isM2MSupportedLanguage = (language?: string | null): boolean =>
  M2M_SUPPORTED_LANGUAGE_KEYS.has(canonicalizeLanguageKey(language));

export const M2M_TARGET_LANGUAGE_OPTIONS = M2M_SUPPORTED_LANGUAGES.map((value) => ({
  label: value,
  value,
}));

export const getVerifiedCompletedTranslationPairs = (
  translationPairs: string[] = [],
  translationProgress?: TranslationProgressMap
): string[] => {
  if (translationProgress === undefined) return translationPairs;
  const normalizedProgress = canonicalizeTranslationProgressMap(translationProgress);
  if (!normalizedProgress) return [];

  return translationPairs.filter((pair) => {
    const targetLanguage = getTargetLanguageFromPair(pair);
    const targetKey = canonicalizeLanguageKey(targetLanguage);
    const entry = normalizedProgress[canonicalizeLanguageName(targetLanguage)] ||
      Object.entries(normalizedProgress).find(([progressLanguage]) => {
        const progressTarget = getTargetLanguageFromPair(progressLanguage) || progressLanguage;
        return canonicalizeLanguageKey(progressTarget) === targetKey;
      })?.[1];
    if (!entry) return false;
    return entry.status === 'complete' || entry.percentage >= 100 || (
      entry.total_chapters > 0 && entry.translated_chapters >= entry.total_chapters
    );
  });
};

const M2M_REPORT_BLOCKING_STATUSES = new Set([
  'created',
  'awaiting_payment',
  'paid',
  'processing',
  'completed',
]);

export const isM2MReportBlockingStatus = (status?: string | null): boolean =>
  M2M_REPORT_BLOCKING_STATUSES.has((status || '').trim().toLowerCase());

export const getM2MBlockingHandoff = (
  handoffs: M2MHandoffRecord[],
  language?: string | null
): M2MHandoffRecord | undefined => {
  const languageKey = canonicalizeLanguageKey(language);
  if (!languageKey) return undefined;

  return handoffs.find(
    (handoff) =>
      isM2MReportBlockingStatus(handoff.status) &&
      (handoff.targetLanguages || []).some(
        (targetLanguage) => canonicalizeLanguageKey(targetLanguage) === languageKey
      )
  );
};
